import * as React from 'react';
import { assertNever } from 'owa-assert';
import { FileSelectionFilter } from 'owa-fileprovider-store';
import { observer } from 'mobx-react';

import styles from './LocalComputerFilePicker.scss';

export interface LocalComputerFilePickerProps {
    onFilesSelected: (files: File[]) => void;
    fileSelectionFilter: FileSelectionFilter;
}

export interface ILocalComputerFilePicker {
    click: () => void;
}

@observer
export default class LocalComputerFilePicker
    extends React.Component<LocalComputerFilePickerProps>
    implements ILocalComputerFilePicker {
    private fileInputDiv: HTMLInputElement;

    render() {
        return (
            <input
                type="file"
                accept={this.getFileInputAcceptType(this.props.fileSelectionFilter)}
                className={styles.hidden}
                ref={(ref: HTMLInputElement) => (this.fileInputDiv = ref)}
                onChange={this.onFilesChange}
                value=""
                multiple={true}
            />
        );
    }

    click() {
        this.fileInputDiv.click();
    }

    private getFileInputAcceptType(filterType: FileSelectionFilter) {
        switch (filterType) {
            case FileSelectionFilter.None:
                // In case of None we return undefined so `accept` attribute is not set.
                // This will make the default behavior of any file to be shown.
                return undefined;
            case FileSelectionFilter.Images:
                return 'image/*';
            default:
                return assertNever(filterType);
        }
    }

    private onFilesChange = (event: React.FormEvent<HTMLInputElement>) => {
        // We decided to use File[] instead of FileList in our FilePicker code because the first has some
        // limitations like not supporting many of the iteration methods that array provides (ForEach, Map, etc).
        // Given that it is a system type, it is also harder to mock from a unit test perspective.
        const fileList: FileList = (event.target as HTMLInputElement).files;

        // Bug in IE - it will trigger the same event with 0 file
        // Don't do anything if the file list has nothing
        if (fileList.length > 0) {
            const files: File[] = [];
            for (let i = 0; i < fileList.length; i++) {
                files.push(fileList[i]);
            }

            this.clearLocalComputerFilePickerValue();
            this.props.onFilesSelected(files);
        }
    };

    private clearLocalComputerFilePickerValue = () => {
        // If we don't set the value to empty then the onChange event
        // would not trigger if the same file is selected as nothing
        // has changed. Hence we clear the attribute 'value' so that
        // even if the same file is selected again the event is triggered.
        this.fileInputDiv.setAttribute('value', '');
    };
}
